import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

export default function UserForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff' });
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isEdit) {
            api.get('/users')
                .then(r => {
                    const u = r.data.find(u => u.id === parseInt(id));
                    if (u) setForm({ name: u.name, email: u.email, password: '', role: u.role });
                })
                .catch(() => toast.error('Failed to load user'))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (isEdit && !payload.password) delete payload.password;
            if (isEdit) await api.put(`/users/${id}`, payload);
            else await api.post('/users', payload);
            toast.success(isEdit ? 'User updated' : 'User created');
            setTimeout(() => navigate('/admin/users'), 800);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <Toaster position="top-right" richColors />
            <div className="max-w-md mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => navigate('/admin/users')} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                    <h2 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit User' : 'Add User'}</h2>
                </div>
                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                        <input 
                            type="text" 
                            value={form.name} 
                            onChange={e => setForm(p => ({ ...p, name: e.target.value }))} 
                            required 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                        <input 
                            type="email" 
                            value={form.email} 
                            onChange={e => setForm(p => ({ ...p, email: e.target.value }))} 
                            required 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Password {isEdit && <span className="text-gray-400 font-normal">(leave blank to keep current)</span>}
                            {!isEdit && <span className="text-red-500"> *</span>}
                        </label>
                        <input 
                            type="password" 
                            value={form.password} 
                            onChange={e => setForm(p => ({ ...p, password: e.target.value }))} 
                            required={!isEdit} 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                        <select 
                            value={form.role} 
                            onChange={e => setForm(p => ({ ...p, role: e.target.value }))} 
                            style={{ color: '#000000', backgroundColor: '#ffffff', colorScheme: 'light' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        >
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50">{saving ? 'Saving…' : isEdit ? 'Update User' : 'Create User'}</button>
                        <button type="button" onClick={() => navigate('/admin/users')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
